import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, Globe, Mail, Phone, MapPin, User, Users, Unlink, MoreVertical } from 'lucide-react';
import { useWorkspace } from '@/context/WorkspaceContext';
import { projectService } from '@/lib/projectService';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Check, Crown, Edit2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface CompanyDetailProps {
  id?: string;
  onBack?: () => void;
}

export const CompanyDetail: React.FC<CompanyDetailProps> = ({ id: propId, onBack }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = propId || paramId;
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspace();
  const [company, setCompany] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [allWorkspaceClients, setAllWorkspaceClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkClientsOpen, setLinkClientsOpen] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [linkingClients, setLinkingClients] = useState(false);
  const [, setUnlinkingId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<{ id: string, role: string } | null>(null);

  useEffect(() => {
    if (id && currentWorkspace) {
      loadData();
    }
  }, [id, currentWorkspace]);

  const loadData = async () => {
    if (!id || !currentWorkspace) return;
    try {
      setLoading(true);
      const companyData = await projectService.getCompanyById(id);
      setCompany(companyData);
      
      const clientsData = await projectService.getCompanyClients(id);
      setClients(clientsData);

      const workspaceClients = await projectService.getWorkspaceClients(currentWorkspace.id);
      setAllWorkspaceClients(workspaceClients.filter(c => c.companyId !== id)); // exclude already linked
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClients = async () => {
    if (!currentWorkspace || !id || selectedClientIds.length === 0) return;
    try {
      setLinkingClients(true);
      await projectService.linkClientsToCompany(currentWorkspace.id, id, selectedClientIds);
      toast.success('Clients linked successfully');
      setLinkClientsOpen(false);
      setSelectedClientIds([]);
      loadData(); // reload
    } catch (error) {
      console.error('Error linking clients:', error);
      toast.error('Failed to link clients');
    } finally {
      setLinkingClients(false);
    }
  };

  const handleUnlinkClient = async (userId: string) => {
    if (!id) return;
    try {
      setUnlinkingId(userId);
      await projectService.unlinkClientFromCompany(id, userId);
      toast.success('Client unlinked successfully');
      loadData(); // reload clients list
    } catch (error) {
      console.error('Error unlinking client:', error);
      toast.error('Failed to unlink client');
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    if (!id || !newRole.trim()) {
      setEditingRole(null);
      return;
    }
    try {
      await projectService.updateCompanyClientRole(id, userId, newRole.trim());
      toast.success('Role updated successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to update role');
    }
    setEditingRole(null);
  };

  const handleTransferOwnership = async (newOwnerId: string) => {
    if (!id) return;
    try {
      await projectService.transferCompanyOwnership(id, newOwnerId);
      toast.success('Ownership transferred successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to transfer ownership');
    }
  };

  if (loading) {
    return null;
  }

  if (!company) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <Building2 className="w-12 h-12 text-muted-foreground opacity-50" />
        <h2 className="text-xl font-semibold">Company not found</h2>
        <Button variant="outline" onClick={() => {
          if (onBack) onBack();
          else navigate('/crm/clients?tab=companies');
        }}>
          Back to CRM
        </Button>
      </div>
    );
  }

  const formatAddress = () => {
    const parts = [company.street_address, company.city, company.state, company.zip, company.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const address = formatAddress();

  return (
    <div className="w-full">
      <button 
        onClick={() => {
          if (onBack) onBack();
          else navigate('/crm/clients?tab=companies');
        }}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Companies
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info Column */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-workspace-accent/10 flex items-center justify-center text-workspace-accent overflow-hidden shrink-0">
                {company.logo_url ? (
                  <img src={company.logo_url} alt={company.name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-8 h-8" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{company.name}</h1>
                {company.category && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground mt-1">
                    {company.category}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {company.website && (
                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Website</div>
                    <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:underline">
                      {company.website}
                    </a>
                  </div>
                </div>
              )}
              {company.email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Email</div>
                    <a href={`mailto:${company.email}`} className="text-sm text-blue-500 hover:underline">
                      {company.email}
                    </a>
                  </div>
                </div>
              )}
              {company.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Phone</div>
                    <a href={`tel:${company.phone}`} className="text-sm text-blue-500 hover:underline">
                      {company.phone}
                    </a>
                  </div>
                </div>
              )}
              {address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Address</div>
                    <div className="text-sm text-muted-foreground">{address}</div>
                  </div>
                </div>
              )}
              {company.agentName && (
                <div className="flex items-start gap-3 pt-4 border-t border-border">
                  <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">Assigned Agent</div>
                    <div className="text-sm text-muted-foreground">{company.agentName}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clients Column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Users className="w-5 h-5 text-workspace-accent" />
                Linked Clients
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full font-medium">
                  {clients.length}
                </span>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="h-8 gap-1.5"
                  onClick={() => setLinkClientsOpen(true)}
                >
                  <Plus className="w-3.5 h-3.5" /> Link Clients
                </Button>
              </div>
            </div>

            {clients.length > 0 ? (
              <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
                {clients.map(client => (
                  <div key={client.id} className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors bg-card">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-workspace-accent/15 flex items-center justify-center text-sm font-semibold text-workspace-accent overflow-hidden shrink-0 cursor-pointer" onClick={() => setSearchParams({ tab: 'clients', clientId: client.id })}>
                        {client.avatar ? (
                          <img src={client.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          (client.name[0] || 'C').toUpperCase()
                        )}
                      </div>
                      <div className="cursor-pointer group/name" onClick={() => setSearchParams({ tab: 'clients', clientId: client.id })}>
                        <div className="font-medium text-foreground group-hover/name:text-workspace-accent transition-colors">{client.name}</div>
                        <div className="text-sm text-muted-foreground">{client.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {editingRole?.id === client.id ? (
                        <div className="flex items-center gap-2">
                          <Input 
                            autoFocus
                            value={editingRole?.role || ''}
                            onChange={(e) => setEditingRole(prev => prev ? { ...prev, role: e.target.value } : null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && editingRole) handleUpdateRole(client.id, editingRole.role);
                              if (e.key === 'Escape') setEditingRole(null);
                            }}
                            className="h-7 text-xs w-32"
                          />
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => editingRole && handleUpdateRole(client.id, editingRole.role)}>
                            <Check className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
                          client.isOwner 
                            ? 'bg-amber-500/15 text-amber-600 border border-amber-500/30' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {client.isOwner && <Crown className="w-3.5 h-3.5" />}
                          {client.role || 'Member'}
                        </span>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => setEditingRole({ id: client.id, role: client.role || 'Member' })}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Role
                          </DropdownMenuItem>
                          {!client.isOwner && (
                            <DropdownMenuItem onClick={() => handleTransferOwnership(client.id)}>
                              <Crown className="w-4 h-4 mr-2" />
                              Make Owner
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleUnlinkClient(client.id)}
                            className="text-red-500 focus:text-red-600"
                          >
                            <Unlink className="w-4 h-4 mr-2" />
                            Unlink Client
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-border rounded-lg bg-muted/20">
                <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3 opacity-50" />
                <h3 className="text-base font-medium text-foreground mb-1">No clients linked</h3>
                <p className="text-sm text-muted-foreground">This company doesn't have any clients yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={linkClientsOpen} onOpenChange={setLinkClientsOpen}>
        <DialogContent className="sm:max-w-[425px] bg-card border-border">
          <DialogHeader>
            <DialogTitle>Link Existing Clients</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
            {allWorkspaceClients.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-4">
                No unlinked clients available in this workspace.
              </div>
            ) : (
              allWorkspaceClients.map(client => (
                <div
                  key={client.id}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors border ${
                    selectedClientIds.includes(client.id)
                      ? 'bg-workspace-accent/10 border-workspace-accent/30 text-foreground'
                      : 'bg-transparent border-transparent text-muted-foreground hover:bg-muted/50'
                  }`}
                  onClick={() => {
                    setSelectedClientIds(prev => 
                      prev.includes(client.id) ? prev.filter(id => id !== client.id) : [...prev, client.id]
                    );
                  }}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                    selectedClientIds.includes(client.id) 
                      ? 'bg-workspace-accent border-workspace-accent text-white' 
                      : 'border-input bg-background'
                  }`}>
                    {selectedClientIds.includes(client.id) && <Check className="w-3 h-3" />}
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {client.avatar ? (
                      <img src={client.avatar} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="w-3 h-3 text-muted-foreground" />
                      </div>
                    )}
                    <div className="truncate">
                      <p className="text-sm font-medium leading-none truncate">{client.name}</p>
                      {client.email && <p className="text-xs text-muted-foreground truncate">{client.email}</p>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkClientsOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleLinkClients} 
              disabled={linkingClients || selectedClientIds.length === 0}
              className="bg-workspace-accent hover:bg-workspace-accent-hover text-white"
            >
              {linkingClients ? 'Linking...' : 'Link Selected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetail;
